import { Box, Typography, Button, Link, Tooltip } from '@mui/material';
import useTranslation from 'next-translate/useTranslation';
import { FC } from 'react';
import { BUTTON_CLASS, DOT_CLASS, WRAPPER_CLASS, containerSx, dotSx, textSx } from './styles';
import { TimelinePositioning, Variant } from './types';

interface ClickableEntryProps {
    content: string | JSX.Element;
    dataTestId?: string;
    disabled?: boolean;
    href?: string;
    isActive?: boolean;
    onClick?: () => void;
    positioning?: TimelinePositioning;
    title: string;
    variant?: Variant;
}

const ClickableEntry: FC<ClickableEntryProps> = ({
    dataTestId,
    disabled = false,
    isActive = false,
    content,
    href,
    onClick,
    positioning,
    title,
    variant,
}) => {
    const { t } = useTranslation('decision');
    const isSelected = Boolean(variant);
    const { fullHeight, elementHeight, level, xPos, yPos } = positioning ?? {};
    const bottomShift =
        typeof fullHeight === 'number' &&
        typeof yPos === 'number' &&
        typeof elementHeight === 'number'
            ? fullHeight - yPos - elementHeight
            : undefined;
    const { contentSx, titleSx } = textSx(isActive);

    const buttonElement = (
        <Button
            className={BUTTON_CLASS}
            component={Link}
            disabled={disabled}
            href={onClick ? undefined : href}
            onClick={onClick}
            sx={{ display: 'block' }}
        >
            <Typography sx={titleSx}>{title}</Typography>
            <Typography sx={contentSx}>{content}</Typography>
        </Button>
    );

    return (
        <Box
            data-test-id={dataTestId}
            sx={containerSx(Number(xPos), Number(bottomShift), {
                disabled,
                isActive,
                level: Number(level),
                variant,
            })}
        >
            <Box className={WRAPPER_CLASS}>
                {disabled ? (
                    <Tooltip
                        componentsProps={{ tooltip: { sx: { p: 0.5 } } }}
                        placement="top"
                        title={t('timeline.notInDatabase')}
                    >
                        <span>{buttonElement}</span>
                    </Tooltip>
                ) : (
                    buttonElement
                )}
            </Box>
            <Box className={DOT_CLASS} sx={dotSx({ isActive, isSelected }, Number(bottomShift))} />
        </Box>
    );
};

export default ClickableEntry;
